import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getWhatsAppContact } from "@/lib/contact";
import { COOKIE_NOTICE_DISMISSED_EVENT } from "./CookieNotice";

const DISMISSED_KEY = "vvs_cookie_notice_dismissed";

/**
 * Élément flottant WhatsApp (VVS_MODELE_INTERFACE.md §1.10) : bas-droite,
 * au-dessus de la barre d'onglets basse mobile. Numéro lu dynamiquement
 * depuis `setting.support.whatsapp` (cf. `@/lib/contact`) — jamais codé en
 * dur.
 *
 * Fusionne apps/web/components/layout/WhatsAppFloatButton.tsx (Server
 * Component, lisait le setting) et WhatsAppFloatButtonClient.tsx (positionnement
 * client) en un seul composant client : il n'y a plus de rendu serveur ici, la
 * lecture du setting passe donc par un hook TanStack Query comme le reste des
 * données publiques de ce SPA.
 */
export function WhatsAppFloatButton() {
  const { data } = useQuery({
    queryKey: ["settings", "whatsapp-contact"],
    queryFn: getWhatsAppContact,
    // getWhatsAppContact() a déjà son propre repli interne (jamais de rejet) —
    // c'est le typage de useQuery qui exige quand même un queryFn "propre".
  });
  const link = data?.link ?? "#";

  const [isCookieNoticeVisible, setIsCookieNoticeVisible] = useState(false);

  useEffect(() => {
    try {
      setIsCookieNoticeVisible(window.localStorage.getItem(DISMISSED_KEY) !== "1");
    } catch {
      setIsCookieNoticeVisible(true);
    }

    function handleDismiss() {
      setIsCookieNoticeVisible(false);
    }
    window.addEventListener(COOKIE_NOTICE_DISMISSED_EVENT, handleDismiss);
    return () => window.removeEventListener(COOKIE_NOTICE_DISMISSED_EVENT, handleDismiss);
  }, []);

  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Nous contacter sur WhatsApp"
      className={[
        "fixed right-4 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg transition-[bottom] duration-200 hover:scale-105 lg:right-6",
        isCookieNoticeVisible ? "bottom-[180px] lg:bottom-24" : "bottom-20 lg:bottom-6",
      ].join(" ")}
    >
      <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.79.47 3.48 1.34 4.94L2 22l5.25-1.38c1.42.78 3.03 1.2 4.79 1.2h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.51 2 12.04 2Zm5.63 14.16c-.24.68-1.19 1.24-1.94 1.4-.53.11-1.22.2-3.55-.76-2.98-1.24-4.9-4.24-5.05-4.44-.15-.2-1.2-1.6-1.2-3.05 0-1.45.76-2.16 1.03-2.46.27-.3.59-.37.79-.37.2 0 .39 0 .56.01.18.01.42-.07.65.5.24.58.82 2.01.89 2.16.07.15.12.33.02.53-.1.2-.15.32-.3.5-.15.18-.31.4-.44.53-.15.15-.3.31-.13.6.17.3.76 1.26 1.64 2.04 1.13.99 2.07 1.3 2.37 1.45.3.15.48.13.65-.08.18-.2.75-.87.95-1.17.2-.3.4-.25.68-.15.28.1 1.76.83 2.06.98.3.15.5.23.57.35.08.13.08.72-.16 1.4Z" />
      </svg>
    </a>
  );
}
